import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { LogoutMutationResponse } from "../types/Logout";
import type { UseMutationOptions } from "@tanstack/react-query";

 type LogoutClient = typeof client<LogoutMutationResponse, never, never>;
type Logout = {
    data: LogoutMutationResponse;
    error: never;
    request: never;
    pathParams: never;
    queryParams: never;
    headerParams: never;
    response: LogoutMutationResponse;
    client: {
        parameters: Partial<Parameters<LogoutClient>[0]>;
        return: Awaited<ReturnType<LogoutClient>>;
    };
};
/**
 * @description This endpoint will end any backend client sessions that are currently active. Only callable using the JWT token
authentication scheme.
            
This does not invalidate the current JWT token, it is up to the client to do that.
 * @summary User Logout
 * @link /api/auth/logout
 */
export function useLogout(options: {
    mutation?: UseMutationOptions<Logout["response"], Logout["error"], Logout["request"]>;
    client?: Logout["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async () => {
            const res = await client<Logout["data"], Logout["error"], Logout["request"]>({
                method: "post",
                url: `/api/auth/logout`,
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}